/**
 * Quality warning toast component.
 * Displays warnings to respondents about quality issues.
 */

import { useEffect, useState, useCallback } from 'react';
import { useQualityStore } from '@/stores/quality-store';
import { useSessionStore } from '@/stores/session-store';

/**
 * Alert icon component.
 */
function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

/**
 * Close icon component.
 */
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

/**
 * Quality warning toast.
 */
export function QualityWarningToast() {
  const { activeWarning, dismissActiveWarning } = useQualityStore();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (activeWarning && !activeWarning.dismissed) {
      setIsVisible(true);
      setIsLeaving(false);
    } else if (isVisible) {
      setIsLeaving(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsLeaving(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeWarning, isVisible]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      dismissActiveWarning();
    }, 300);
  };

  if (!isVisible || !activeWarning) {
    return null;
  }

  return (
    <div
      className={`
        fixed top-4 left-1/2 -translate-x-1/2 z-50
        max-w-md w-full mx-4
        transition-all duration-300 ease-out
        ${isLeaving ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}
      `}
      role="alert"
      aria-live="assertive"
    >
      <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertIcon className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">
              Please Note
            </p>
            <p className="mt-1 text-sm text-amber-700">
              {activeWarning.message}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-md text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-colors"
            aria-label="Dismiss warning"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Outcome code mapping for user-friendly display.
 */
const OUTCOME_MESSAGES: Record<string, string> = {
  quality_fail: 'Quality requirements not met',
  speeding: 'Survey completed too quickly',
  straightlining: 'Response pattern detected',
  reentry: 'Duplicate participation detected',
  attention_check: 'Attention check failed',
  ip_risk: 'Connection issue detected',
};

/**
 * Build redirect URL with outcome codes.
 */
export function buildRedirectUrl(
  baseUrl: string,
  outcome: string,
  reason?: string,
  sessionId?: string,
): string {
  try {
    const url = new URL(baseUrl);

    // Add outcome code
    url.searchParams.set('outcome', outcome);

    // Add reason if provided (sanitized)
    if (reason) {
      // Extract main reason keyword
      const reasonCode = extractReasonCode(reason);
      url.searchParams.set('reason', reasonCode);
    }

    // Add session ID for tracking
    if (sessionId) {
      url.searchParams.set('sid', sessionId);
    }

    // Add timestamp
    url.searchParams.set('ts', Date.now().toString());

    return url.toString();
  } catch {
    // If URL parsing fails, append as query string
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}outcome=${outcome}`;
  }
}

/**
 * Extract a clean reason code from the termination reason.
 */
function extractReasonCode(reason: string): string {
  const lowerReason = reason.toLowerCase();

  if (lowerReason.includes('speed') || lowerReason.includes('quick')) {
    return 'speeding';
  }
  if (lowerReason.includes('straightlin') || lowerReason.includes('pattern')) {
    return 'straightlining';
  }
  if (lowerReason.includes('duplicate') || lowerReason.includes('re-entry') || lowerReason.includes('reentry')) {
    return 'reentry';
  }
  if (lowerReason.includes('attention')) {
    return 'attention_check';
  }
  if (lowerReason.includes('ip') || lowerReason.includes('vpn') || lowerReason.includes('proxy')) {
    return 'ip_risk';
  }
  if (lowerReason.includes('terminated')) {
    return 'terminated';
  }

  return 'quality_fail';
}

/**
 * Quality termination overlay.
 * Shown when session is being terminated due to quality fail.
 * Shows countdown with redirect, or fallback screen if no redirect URL.
 */
export function QualityTerminationOverlay() {
  const { isTerminating, terminationReason, riskFlags } = useQualityStore();
  const { session, sessionId, endSummary } = useSessionStore();
  const [countdown, setCountdown] = useState(5);
  const [phase, setPhase] = useState<'countdown' | 'redirecting' | 'fallback'>('countdown');

  // Determine if we have a redirect URL
  const returnUrl = endSummary?.returnUrl || session?.returnUrlQualityFail;
  const hasRedirectUrl = !!returnUrl;

  // Build the redirect URL with outcome codes
  const getEnhancedRedirectUrl = useCallback(() => {
    if (!returnUrl) return null;

    const outcome = endSummary?.outcome || 'quality_fail';
    const reason = terminationReason || endSummary?.outcomeReason;

    return buildRedirectUrl(returnUrl, outcome, reason, sessionId || undefined);
  }, [returnUrl, endSummary, terminationReason, sessionId]);

  // Get user-friendly message for the reason
  const getReasonDisplay = useCallback(() => {
    if (!terminationReason) return null;

    const code = extractReasonCode(terminationReason);
    return OUTCOME_MESSAGES[code] || terminationReason;
  }, [terminationReason]);

  useEffect(() => {
    if (!isTerminating) {
      setCountdown(5);
      setPhase('countdown');
      return;
    }

    // If no redirect URL, show fallback immediately after countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          if (hasRedirectUrl) {
            setPhase('redirecting');
            // Perform redirect with outcome codes
            const redirectUrl = getEnhancedRedirectUrl();
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          } else {
            setPhase('fallback');
          }

          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTerminating, hasRedirectUrl, getEnhancedRedirectUrl]);

  if (!isTerminating) {
    return null;
  }

  // Fallback screen - shown when no redirect URL is available
  if (phase === 'fallback') {
    return (
      <div
        className="fixed inset-0 z-50 bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="termination-title"
        aria-describedby="termination-description"
      >
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h2
            id="termination-title"
            className="text-2xl font-semibold text-gray-900 mb-3"
          >
            Survey Session Ended
          </h2>
          <p
            id="termination-description"
            className="text-gray-600 mb-6 leading-relaxed"
          >
            Thank you for your time. Unfortunately, we were unable to complete this survey session.
          </p>

          {getReasonDisplay() && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">
                {getReasonDisplay()}
              </p>
            </div>
          )}

          <div className="border-t border-gray-100 pt-6">
            <p className="text-sm text-gray-400 mb-4">
              You may close this window now.
            </p>
            <button
              onClick={() => window.close()}
              className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              Close Window
            </button>
          </div>

          {/* Session reference for support */}
          {sessionId && (
            <p className="mt-6 text-xs text-gray-300">
              Reference: {sessionId.slice(0, 8)}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Countdown/redirecting phase
  return (
    <div
      className="fixed inset-0 z-50 bg-gray-900/80 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="termination-title"
      aria-describedby="termination-description"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <AlertIcon className="h-6 w-6 text-red-600" />
        </div>
        <h2
          id="termination-title"
          className="text-xl font-semibold text-gray-900 mb-2"
        >
          Survey Ended
        </h2>
        <p
          id="termination-description"
          className="text-gray-600 mb-4"
        >
          Unfortunately, we are unable to continue with this survey at this time.
        </p>

        {getReasonDisplay() && (
          <p className="text-sm text-gray-500 mb-4">
            {getReasonDisplay()}
          </p>
        )}

        {phase === 'countdown' && hasRedirectUrl && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <svg
              className="animate-spin h-4 w-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Redirecting in {countdown} seconds...</span>
          </div>
        )}

        {phase === 'redirecting' && (
          <p className="text-sm text-gray-400">
            Redirecting now...
          </p>
        )}

        {/* Risk flags for debugging (hidden in production) */}
        {import.meta.env.DEV && riskFlags.length > 0 && (
          <div className="mt-4 p-2 bg-gray-50 rounded text-xs text-gray-400">
            Flags: {riskFlags.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Combined quality UI components.
 */
export function QualityUI() {
  return (
    <>
      <QualityWarningToast />
      <QualityTerminationOverlay />
    </>
  );
}
